import { Pencil, Trash2 } from "lucide-react";
import { useMemo } from "react";

import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { Loader } from "@/components/common/Loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useProvisions } from "@/features/provisions/hooks/useProvisions";
import type { RecipeDetailsModalProps } from "@/features/recipes/types";
import { formatQuantity } from "@/lib/format";

export default function RecipeDetailsDialog({
  recipe,
  onClose,
  formatDate,
  onEdit,
  onDelete,
}: RecipeDetailsModalProps) {
  const { provisions, isLoading, error, refresh } = useProvisions();

  const byId = useMemo(() => new Map(provisions.map((provision) => [provision.id, provision])), [provisions]);

  const ingredients = recipe.ingredients ?? [];

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{recipe.name}</DialogTitle>
          <DialogDescription>{recipe.description || "Aucune description"}</DialogDescription>
        </DialogHeader>

        {recipe.createdAt ? (
          <p className="text-sm text-muted-foreground">
            Créée le <span className="text-foreground">{formatDate(recipe.createdAt)}</span>
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <h3 className="text-[15px] font-semibold">
            Matières premières consommées
            {ingredients.length > 0 ? ` (${ingredients.length})` : ""}
          </h3>

          {isLoading && <Loader />}

          {!isLoading && error && <ErrorState message={error} onRetry={() => refresh()} />}

          {!isLoading && !error && ingredients.length === 0 && (
            <EmptyState title="Aucun ingrédient" hint="Cette recette ne consomme aucune matière première." />
          )}

          {!isLoading && !error && ingredients.length > 0 && (
            <ul className="overflow-hidden rounded-xl border border-border bg-card">
              {ingredients.map((item) => {
                const provision = byId.get(item.provisionId);
                return (
                  <li
                    key={item.provisionId}
                    className="flex items-center justify-between gap-3 border-b border-muted px-4 py-3 last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 truncate text-[15px]">{provision?.name ?? "Matière inconnue"}</span>
                    <span className="font-medium tabular-nums">
                      {formatQuantity(item.quantity, provision?.measurementUnit)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="gap-3 sm:gap-2">
          <Button type="button" variant="destructive" size="lg" onClick={onDelete}>
            <Trash2 />
            Supprimer
          </Button>
          <Button type="button" size="lg" onClick={onEdit}>
            <Pencil />
            Modifier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
